// ── GameScoreHeader — centered score + status strip for GameDetail ──
//
// Stripped down to just the data the page header doesn't already show:
//   • Centered AWAY  N — N  HOME score block (large, prominent)
//   • Status badge above the score (LIVE / FINAL / pre-game countdown)
//   • Period tag for live games (e.g. "B · 15" for MLB bottom of 5th)
//
// The sport breadcrumb + matchup title that used to live here moved
// out — the page-level header already shows MLB · 2026-05-22 and
// "HOU @ CHC" so repeating them was noise. Pre-snapshot fallback uses
// the URL team codes so the card has shape during the connect window.
//
// Status mapping (HYDRA_API `gameState.statusId`):
//   1 = scheduled (pre)
//   2 = live
//   3 = final
//
// `gameClock` is sport-specific (MLB: "T"/"B" for top/bottom half-inning;
// basketball: minutes remaining; etc.) — surfaced verbatim alongside
// `periodTypeId` for context.

import { NearEventStartPulse } from '../ValueDashboard/GameTimeIndicators';
import { useNow } from '@/lib/infra/useNow';
import { formatGameStatus, isConfirmedFinal } from '@/lib/sport/periodLabel';
import { colorForSide, deriveWinnerSide, rankColor } from './teamColors';
import type {
  OddsStreamConsensus,
  OddsStreamGame,
  OddsStreamGameState,
} from '../../services/oddsStream';

interface Props {
  /** From snapshot.game when the snapshot has arrived. Null during the
   *  connect window — falls back to fallbackTeams + scheduledStartMs. */
  game: OddsStreamGame | null;
  /** Latest gameState event (or snapshot.gameState on first paint). */
  gameState: OddsStreamGameState | null;
  /** Latest Full-Game consensus — drives the winner-based team-name
   *  color (>50¢ team gets green, the other gets blue). Shares the
   *  exact same logic as the chart's line colors so the two surfaces
   *  stay synchronized when the leader flips. */
  latestConsensus: OddsStreamConsensus | null;
  /** Fallback team codes from the URL — shown before snapshot arrives. */
  fallbackTeams: readonly [string, string];
  /** Scheduled event start epoch ms (from marketStore.getEventStartTimeMsByTicker
   *  when available). Drives the countdown + near-start pulse on pre-games. */
  scheduledStartMs: number | null;
}

export function GameScoreHeader({ game, gameState, latestConsensus, fallbackTeams, scheduledStartMs }: Props) {
  const awayCode = game?.awayTeam ?? fallbackTeams[0];
  const homeCode = game?.homeTeam ?? fallbackTeams[1];
  const statusId = gameState?.statusId ?? null;
  const awayScore = typeof gameState?.awayScore === 'number' ? gameState.awayScore : null;
  const homeScore = typeof gameState?.homeScore === 'number' ? gameState.homeScore : null;
  const isLive = statusId === 2;
  // FINAL requires the score (isConfirmedFinal) — a statusId 3 with null
  // scores is a stale/garbage record (soccer gameStates, 2026-06-12), and a
  // scoreless FINAL banner on a live game is worse than the pre-game
  // fallback.
  const isFinal = isConfirmedFinal(gameState);
  const isPre = !isFinal && !isLive;
  const periodLabel = formatGameStatus(gameState, game?.sport);
  const showScore = (isLive || isFinal) && awayScore !== null && homeScore !== null;

  // Mirror the chart's color logic — green for whoever's currently
  // leading the consensus (or whoever won the final). The two surfaces
  // import the same helpers so any flip is in lockstep. Soccer 3-way
  // (drawFairProbCents present) uses rank colors across all three
  // outcomes — a team can render grey when the Draw outranks it.
  const drawCents = latestConsensus?.drawFairProbCents ?? null;
  const rankAll =
    latestConsensus != null && drawCents != null
      ? [latestConsensus.awayFairProbCents, latestConsensus.homeFairProbCents, drawCents]
      : null;
  const winnerSide = deriveWinnerSide(latestConsensus, gameState);
  const awayColor =
    rankAll && latestConsensus
      ? rankColor(latestConsensus.awayFairProbCents, rankAll)
      : colorForSide('away', winnerSide);
  const homeColor =
    rankAll && latestConsensus
      ? rankColor(latestConsensus.homeFairProbCents, rankAll)
      : colorForSide('home', winnerSide);

  return (
    // No card chrome here — parent (GameDetail) wraps this together
    // with <ConsensusChart> in a single shared container so the score
    // header and the chart visually read as one block tied to the
    // game, not two cards that look independent of the bet selections
    // below.
    <div className="px-4 py-5">
      <div className="flex flex-col items-center gap-3">
        {/* Status badge row — only renders for live + final. Pre-game
            puts its own time/countdown strip BELOW the matchup so the
            team names land at the top of the card. */}
        {(isLive || isFinal) && (
          <div>
            {isLive ? (
              <span className="inline-flex items-center gap-1.5 rounded bg-green-500/15 px-2.5 py-1 text-[11px] font-semibold uppercase tracking-wider text-green-400">
                <span className="inline-block h-1.5 w-1.5 animate-pulse rounded-full bg-green-500" />
                LIVE
                {periodLabel && <span className="text-foreground/80 ml-1">{periodLabel}</span>}
              </span>
            ) : (
              <span className="rounded bg-zinc-500/15 px-2.5 py-1 text-[11px] font-semibold uppercase tracking-wider text-zinc-400">
                FINAL
              </span>
            )}
          </div>
        )}

        {/* Big score block. Renders for live + final; pre-game gets the
            prominent matchup display below in its own branch. */}
        {showScore && (
          <div className="flex items-center gap-8">
            <ScoreCell code={awayCode} score={awayScore!} color={awayColor} />
            <span className="text-muted-foreground/40 text-3xl font-light">—</span>
            <ScoreCell code={homeCode} score={homeScore!} color={homeColor} />
          </div>
        )}

        {/* Pre-game: matchup at the top (big), start time + countdown
            in a prominent row below. Team codes carry the chart-matching
            colors so users can correlate at a glance. */}
        {isPre && !showScore && (
          <>
            <div className="flex items-baseline gap-6">
              <span
                className="font-mono text-4xl font-bold tabular-nums"
                style={{ color: awayColor }}
              >
                {awayCode}
              </span>
              <span className="text-muted-foreground/40 text-2xl">@</span>
              <span
                className="font-mono text-4xl font-bold tabular-nums"
                style={{ color: homeColor }}
              >
                {homeCode}
              </span>
            </div>
            <PreGameTimeRow scheduledStartMs={scheduledStartMs} />
          </>
        )}
      </div>
    </div>
  );
}

// ─── PreGameTimeRow — prominent start-time + countdown for pre-games ─

function PreGameTimeRow({ scheduledStartMs }: { scheduledStartMs: number | null }) {
  const now = useNow();
  if (scheduledStartMs == null) {
    return (
      <div className="text-muted-foreground text-xs font-semibold uppercase tracking-wider">
        Scheduled — event start TBD
      </div>
    );
  }
  const diff = scheduledStartMs - now;
  const startTimeStr = new Date(scheduledStartMs).toLocaleTimeString([], {
    hour: 'numeric',
    minute: '2-digit',
  });
  const countdownText = diff > 0 ? formatCountdown(diff) : null;
  return (
    <div className="border-border/60 mt-1 inline-flex items-center gap-3 rounded-lg border bg-muted/30 px-4 py-2">
      {scheduledStartMs != null && <NearEventStartPulse startTimeMs={scheduledStartMs} />}
      <span className="text-foreground font-mono text-lg font-semibold tabular-nums">
        {startTimeStr}
      </span>
      {countdownText && (
        <>
          <span className="text-muted-foreground/50">·</span>
          <span className="text-muted-foreground text-sm">
            starts in{' '}
            <span className="text-foreground font-mono font-semibold tabular-nums">
              {countdownText}
            </span>
          </span>
        </>
      )}
    </div>
  );
}

// Same format as GameCountdown but rendered inline without the
// parentheses — the PreGameTimeRow chrome already provides the
// container styling.
function formatCountdown(diffMs: number): string {
  const totalSec = Math.floor(diffMs / 1000);
  const days = Math.floor(totalSec / 86400);
  if (days >= 1) return `${days}d`;
  const hours = Math.floor(totalSec / 3600);
  const mins = Math.floor((totalSec % 3600) / 60);
  if (hours >= 1) return `${hours}h ${mins}m`;
  const secs = totalSec % 60;
  return `${mins}:${secs.toString().padStart(2, '0')}`;
}

function ScoreCell({ code, score, color }: { code: string; score: number; color: string }) {
  return (
    <div className="flex flex-col items-center gap-1">
      <span
        className="text-sm font-semibold uppercase tracking-wider"
        style={{ color }}
      >
        {code}
      </span>
      <span
        className="text-foreground font-mono text-5xl font-bold tabular-nums"
      >
        {score}
      </span>
    </div>
  );
}

